import { Routes } from '@angular/router';
import { tenantGuard } from './core/guards/tenant.guard';
import { authGuard } from './core/guards/auth.guard';
import { taskGuard } from './core/guards/task.guard';

// JANGAN LUPA TAMBAHIN JUGA DI MENU.TS, BIAR TAMPIL DI SIDEBAR
// src\app\shared\libs\menu.ts
export const routes: Routes = [
  {
    path: '',
    redirectTo: 'pages',
    pathMatch: 'full',
  },
  {
    // "Login with Sokrates" (OIDC) comes back here with the authorization code.
    path: 'auth/sso/callback',
    loadComponent: () =>
      import('./pages/auth/sso-callback/sso-callback.component').then(
        (m) => m.SsoCallbackComponent
      ),
    canActivate: [tenantGuard],
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./pages/auth/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent
      ),
    canActivate: [tenantGuard],
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./pages/maintenance/maintenance.component').then(
        (m) => m.MaintenanceComponent
      ),
  },
  {
    // No guards: this is a 360-assessment rater's emailed magic link, opened with no login
    // and no prior app session (see rater-magic-link.component.ts for why it avoids
    // ConfigService/LocalService/the auth interceptor entirely).
    path: 'rater/:token',
    loadComponent: () =>
      import('./pages/rater-magic-link/rater-magic-link.component').then(
        (m) => m.RaterMagicLinkComponent
      ),
  },
  {
    // No guards: this is the landing page an LTI 1.3 launch redirects the browser to after
    // the backend verifies the LMS's id_token (see handleLtiLaunch on the backend and
    // lti-launch.component.ts for why it avoids ConfigService/LocalService/the auth interceptor).
    path: 'lti/:token',
    loadComponent: () =>
      import('./pages/lti-launch/lti-launch.component').then(
        (m) => m.LtiLaunchComponent
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent
      ),
    canActivate: [tenantGuard],
  },
  {
    path: 'pages',
    loadComponent: () =>
      import('./pages/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [tenantGuard, authGuard],
    canActivateChild: [taskGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
        data: {
          code: 'DASHBOARD',
        },
      },
      {
        path: 'department',
        loadComponent: () =>
          import('./pages/department/department.component').then(
            (m) => m.DepartmentComponent
          ),
        data: {
          tasks: ['DEPARTMENT_VIEW'],
          code: 'DEPARTMENT',
        },
      },
      {
        path: 'competency',
        loadComponent: () =>
          import('./pages/competency/competency.component').then(
            (m) => m.CompetencyComponent
          ),
        data: {
          tasks: ['COMPETENCY_VIEW'],
          code: 'COMPETENCY',
        },
      },
      {
        path: 'position',
        loadComponent: () =>
          import('./pages/position/position.component').then(
            (m) => m.PositionComponent
          ),
        data: {
          tasks: ['POSITION_VIEW'],
          code: 'POSITION',
        },
      },
      {
        path: 'organization-chart',
        loadComponent: () =>
          import(
            './pages/organization-chart/organization-chart.component'
          ).then((m) => m.OrganizationChartComponent),
        data: {
          tasks: ['ORGANIZATION_CHART_VIEW'],
          code: 'ORGANIZATION_CHART',
        },
      },
      {
        path: 'my-schedule',
        loadComponent: () =>
          import('./pages/my-schedule/my-schedule.component').then(
            (m) => m.MyScheduleComponent
          ),
        data: {
          tasks: ['MY_SCHEDULE_VIEW'],
          code: 'MY_SCHEDULE',
        },
      },
      {
        path: 'recruitment',
        children: [
          {
            path: '',
            redirectTo: '/pages/recruitment/dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import(
                './pages/recruitment/recruitment-dashboard/recruitment-dashboard.component'
              ).then((m) => m.RecruitmentDashboardComponent),
            data: {
              tasks: ['RECRUITMENT_DASHBOARD_VIEW'],
              code: 'RECRUITMENT_DASHBOARD',
            },
          },
          {
            path: 'report',
            loadComponent: () =>
              import(
                './pages/recruitment/recruitment-report/recruitment-report.component'
              ).then((m) => m.RecruitmentReportComponent),
            data: {
              tasks: ['RECRUITMENT_REPORT_VIEW'],
              code: 'RECRUITMENT_REPORT',
            },
          },
          {
            path: 'bulk-action',
            loadComponent: () =>
              import(
                './pages/recruitment/bulk-action/bulk-action.component'
              ).then((m) => m.BulkActionComponent),
            data: {
              tasks: ['BULK_ACTION_VIEW'],
              code: 'BULK_ACTION',
            },
          },
          {
            path: 'form-field',
            loadComponent: () =>
              import(
                './pages/recruitment/form-field/form-field.component'
              ).then((m) => m.FormFieldComponent),
            data: {
              tasks: ['FORM_FIELD_VIEW'],
              code: 'FORM_FIELD',
            },
          },
          {
            path: 'form',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/recruitment/form/form-list/form-list.component'
                  ).then((m) => m.FormListComponent),
                data: {
                  tasks: ['FORM_VIEW'],
                  code: 'FORM',
                },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import(
                    './pages/recruitment/form/form-manage/form-manage.component'
                  ).then((m) => m.FormManageComponent),
                data: {
                  tasks: ['FORM_ADD'],
                  code: 'FORM_ADD',
                },
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import(
                    './pages/recruitment/form/form-manage/form-manage.component'
                  ).then((m) => m.FormManageComponent),
                data: {
                  tasks: ['FORM_EDIT'],
                  code: 'FORM_EDIT',
                },
              },
            ],
          },
          {
            path: 'stage',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/recruitment/recruitment-stage/recruitment-stage-list/recruitment-stage-list.component'
                  ).then((m) => m.RecruitmentStageListComponent),
                data: {
                  tasks: ['STAGE_TEMPLATE_VIEW'],
                  code: 'STAGE_TEMPLATE',
                },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import(
                    './pages/recruitment/recruitment-stage/recruitment-stage-manage/recruitment-stage-manage.component'
                  ).then((m) => m.RecruitmentStageManageComponent),
                data: {
                  tasks: ['STAGE_TEMPLATE_ADD'],
                  code: 'STAGE_TEMPLATE_ADD',
                },
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import(
                    './pages/recruitment/recruitment-stage/recruitment-stage-manage/recruitment-stage-manage.component'
                  ).then((m) => m.RecruitmentStageManageComponent),
                data: {
                  tasks: ['STAGE_TEMPLATE_EDIT'],
                  code: 'STAGE_TEMPLATE_EDIT',
                },
              },
            ],
          },
          {
            path: 'job-vacancy',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/recruitment/job-vacancy/job-vacancy-list/job-vacancy-list.component'
                  ).then((m) => m.JobVacancyListComponent),
                data: {
                  tasks: ['JOB_VACANCY_VIEW'],
                  code: 'JOB_VACANCY',
                },
              },
              {
                path: 'detail',
                loadComponent: () =>
                  import(
                    './pages/recruitment/job-vacancy/job-vacancy-detail/job-vacancy-detail.component'
                  ).then((m) => m.JobVacancyDetailComponent),
                data: {
                  tasks: ['JOB_VACANCY_VIEW_DETAIL'],
                  code: 'JOB_VACANCY_DETAIL',
                },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import(
                    './pages/recruitment/job-vacancy/job-vacancy-manage/job-vacancy-manage.component'
                  ).then((m) => m.JobVacancyManageComponent),
                data: {
                  tasks: ['JOB_VACANCY_ADD'],
                  code: 'JOB_VACANCY_ADD',
                },
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import(
                    './pages/recruitment/job-vacancy/job-vacancy-manage/job-vacancy-manage.component'
                  ).then((m) => m.JobVacancyManageComponent),
                data: {
                  tasks: ['JOB_VACANCY_EDIT'],
                  code: 'JOB_VACANCY_EDIT',
                },
              },
            ],
          },
          {
            path: 'candidate-filter-setting',
            loadComponent: () =>
              import(
                './pages/recruitment/candidate-filter-setting/candidate-filter-setting.component'
              ).then((m) => m.CandidateFilterSettingComponent),
            data: {
              tasks: ['CANDIDATE_FILTER_SETTING_VIEW'],
              code: 'CANDIDATE_FILTER_SETTING',
            },
          },
          {
            path: 'chatbot',
            loadComponent: () =>
              import(
                './pages/recruitment-chatbot/recruitment-chatbot.component'
              ).then((m) => m.RecruitmentChatbotComponent),
            data: {
              tasks: ['RECRUITMENT_CHATBOT_VIEW'],
              code: 'RECRUITMENT_CHATBOT',
            },
          },
        ],
      },
      {
        path: 'leave',
        children: [
          {
            path: 'type',
            loadComponent: () =>
              import('./pages/leave/leave-type/leave-type.component').then(
                (m) => m.LeaveTypeComponent
              ),
            data: {
              tasks: ['LEAVE_TYPE_VIEW'],
              code: 'LEAVE_TYPE',
            },
          },
          {
            path: 'balance',
            loadComponent: () =>
              import(
                './pages/leave/leave-balance/leave-balance.component'
              ).then((m) => m.LeaveBalanceComponent),
            data: {
              tasks: ['LEAVE_BALANCE_VIEW'],
              code: 'LEAVE_BALANCE',
            },
          },
          {
            path: 'request',
            loadComponent: () =>
              import(
                './pages/leave/leave-request/leave-request.component'
              ).then((m) => m.LeaveRequestComponent),
            data: {
              tasks: ['LEAVE_REQUEST_VIEW'],
              code: 'LEAVE_REQUEST',
            },
          },
        ],
      },
      {
        path: 'attendance',
        children: [
          {
            path: 'staff-attendance',
            loadComponent: () =>
              import(
                './pages/attendance/staff-attendance/staff-attendance.component'
              ).then((m) => m.StaffAttendanceComponent),
            data: {
              tasks: ['STAFF_ATTENDANCE_VIEW'],
              code: 'STAFF_ATTENDANCE',
            },
          },
          {
            path: 'working-schedule-template',
            loadComponent: () =>
              import(
                './pages/attendance/working-schedule-template/working-schedule-template.component'
              ).then((m) => m.WorkingScheduleTemplateComponent),
            data: {
              tasks: ['WORKING_SCHEDULE_TEMPLATE_VIEW'],
              code: 'WORKING_SCHEDULE_TEMPLATE',
            },
          },
          {
            path: 'staff-position-schedule',
            loadComponent: () =>
              import(
                './pages/attendance/staff-position-schedule/staff-position-schedule.component'
              ).then((m) => m.StaffPositionScheduleComponent),
            data: {
              tasks: ['STAFF_POSITION_SCHEDULE_VIEW'],
              code: 'STAFF_POSITION_SCHEDULE',
            },
          },
          {
            path: 'report',
            loadComponent: () =>
              import(
                './pages/attendance/attendance-report/attendance-report.component'
              ).then((m) => m.AttendanceReportComponent),
            data: {
              tasks: ['ATTENDANCE_REPORT_VIEW'],
              code: 'ATTENDANCE_REPORT',
            },
          },
        ],
      },
      {
        path: 'approval',
        children: [
          {
            path: 'approval-template',
            loadComponent: () =>
              import(
                './pages/approval/approval-template/approval-template.component'
              ).then((m) => m.ApprovalTemplateComponent),
            data: {
              tasks: ['APPROVAL_TEMPLATE_VIEW'],
            },
          },
          {
            path: 'approval-list-old',
            loadComponent: () =>
              import(
                './pages/approval/approval-list/approval-list.component'
              ).then((m) => m.ApprovalListComponent),
            data: {
              tasks: ['APPROVAL_LIST_VIEW'],
            },
          },
          {
            path: 'approval-list',
            loadComponent: () =>
              import(
                './pages/approval/approval-list-v2/approval-list-v2.component'
              ).then((m) => m.ApprovalListV2Component),
            data: {
              tasks: ['APPROVAL_LIST_VIEW'],
            },
          },
        ],
      },
      {
        path: 'job',
        children: [
          {
            path: 'teaching-report',
            loadComponent: () =>
              import(
                './pages/job/teaching-report/teaching-report.component'
              ).then((m) => m.TeachingReportComponent),
            data: {
              tasks: ['TEACHING_REPORT_VIEW'],
              code: 'TEACHING_REPORT',
            },
          },
        ],
      },
      {
        path: 'settings',
        children: [
          {
            path: '',
            redirectTo: '/pages',
            pathMatch: 'full',
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./pages/settings/users//users.component').then(
                (m) => m.UsersComponent
              ),
            data: {
              tasks: ['USER_VIEW'],
            },
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('./pages/settings/roles/roles.component').then(
                (m) => m.RolesComponent
              ),
            data: {
              tasks: ['ROLE_VIEW'],
            },
          },
          {
            path: 'tasks',
            loadComponent: () =>
              import('./pages/settings/tasks/tasks.component').then(
                (m) => m.TasksComponent
              ),
            data: {
              tasks: ['TASK_VIEW'],
            },
          },
          {
            path: 'staff-data-mapping',
            loadComponent: () =>
              import(
                './pages/settings/staff-data-mapping/staff-data-mapping.component'
              ).then((m) => m.StaffDataMappingComponent),
            data: {
              tasks: ['STAFF_DATA_MAPPING_VIEW'],
            },
          },
          {
            path: 'email-template',
            loadComponent: () =>
              import(
                './pages/settings/email-template/email-template.component'
              ).then((m) => m.EmailTemplateComponent),
            data: {
              tasks: ['EMAIL_TEMPLATE_VIEW'],
            },
          },
          {
            path: 'report-setting',
            loadComponent: () =>
              import(
                './pages/settings/report-setting/report-setting.component'
              ).then((m) => m.ReportSettingComponent),
            data: {
              tasks: ['REPORT_SETTING_VIEW'],
            },
          },
          {
            path: 'working-location',
            loadComponent: () =>
              import(
                './pages/settings/working-location/working-location.component'
              ).then((m) => m.WorkingLocationComponent),
            data: {
              tasks: ['WORKING_LOCATION_VIEW'],
            },
          },
          {
            path: 'letter',
            loadComponent: () =>
              import('./pages/settings/letter/letter.component').then(
                (m) => m.LetterComponent
              ),
            data: {
              tasks: ['LETTER_VIEW'],
            },
          },
                  {
                        path: 'recruitment-branding',
                        loadComponent: () =>
                        import('./pages/settings/recruitment-branding/recruitment-branding.component').then((m) => m.RecruitmentBrandingComponent),
                        data: {
                            tasks: ['RECRUITMENT_BRANDING_VIEW'],
                        },
                    },
        ],
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./pages/my-profile/my-profile.component').then(
            (m) => m.MyProfileComponent
          ),
        data: {
          tasks: ['MY_PROFILE_VIEW'],
        },
      },
      {
        path: 'account',
        loadComponent: () =>
          import('./pages/account/account.component').then(
            (m) => m.AccountComponent
          ),
        data: {
          tasks: ['ACCOUNT_VIEW'],
        },
      },
      {
        path: 'email-report',
        loadComponent: () =>
          import('./pages/email-report/email-report.component').then(
            (m) => m.EmailReportComponent
          ),
        data: {
          tasks: ['EMAIL_REPORT_VIEW'],
          code: 'EMAIL_REPORT',
        },
      },
      {
        path: 'background-job',
        loadComponent: () =>
          import('./pages/background-job/background-job.component').then(
            (m) => m.BackgroundJobComponent
          ),
        data: {
          tasks: ['BACKGROUND_JOB_VIEW'],
        },
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./pages/analytics/analytics.component').then(
            (m) => m.AnalyticsComponent
          ),
        data: {
          tasks: ['ANALYTICS_VIEW'],
        },
      },
      {
        path: 'assessment-360',
        loadComponent: () =>
          import('./pages/assessment-360/assessment-360.component').then(
            (m) => m.Assessment360Component
          ),
        data: { code: 'ASSESSMENT_360' },
        children: [
          {
            path: '',
            redirectTo: 'questions',
            pathMatch: 'full',
          },
          {
            path: 'questions',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-question-bank/assessment-360-question-bank.component'
              ).then((m) => m.Assessment360QuestionBankComponent),
            data: { tasks: ['ASSESSMENT_360_QUESTION_MANAGE'] },
          },
          {
            path: 'templates',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-template/assessment-360-template.component'
              ).then((m) => m.Assessment360TemplateComponent),
            data: { tasks: ['ASSESSMENT_360_TEMPLATE_MANAGE'] },
          },
          {
            path: 'templates/new',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-template-form/assessment-360-template-form.component'
              ).then((m) => m.Assessment360TemplateFormComponent),
            data: { tasks: ['ASSESSMENT_360_TEMPLATE_MANAGE'] },
          },
          {
            path: 'templates/:id/edit',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-template-form/assessment-360-template-form.component'
              ).then((m) => m.Assessment360TemplateFormComponent),
            data: { tasks: ['ASSESSMENT_360_TEMPLATE_MANAGE'] },
          },
          {
            path: 'campaigns',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign/assessment-360-campaign.component'
              ).then((m) => m.Assessment360CampaignComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'campaigns/new',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-form/assessment-360-campaign-form.component'
              ).then((m) => m.Assessment360CampaignFormComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'campaigns/:id/edit',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-form/assessment-360-campaign-form.component'
              ).then((m) => m.Assessment360CampaignFormComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'campaigns/:id/monitoring',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-monitoring/assessment-360-campaign-monitoring.component'
              ).then((m) => m.Assessment360CampaignMonitoringComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'campaigns/:id/lti',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-lti/assessment-360-campaign-lti.component'
              ).then((m) => m.Assessment360CampaignLtiComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'scales',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-scale/assessment-360-scale.component'
              ).then((m) => m.Assessment360ScaleComponent),
            data: { tasks: ['ASSESSMENT_360_QUESTION_MANAGE'] },
          },
          {
            path: 'job-levels',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-job-level/assessment-360-job-level.component'
              ).then((m) => m.Assessment360JobLevelComponent),
            data: { tasks: ['ASSESSMENT_360_QUESTION_MANAGE'] },
          },
          {
            path: 'lti-platforms',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-lti-platform/assessment-360-lti-platform.component'
              ).then((m) => m.Assessment360LtiPlatformComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            // employees: who they've been asked to rate
            path: 'my-assessments',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-my-assessments/assessment-360-my-assessments.component'
              ).then((m) => m.Assessment360MyAssessmentsComponent),
            data: { tasks: ['ASSESSMENT_360_MY_ASSESSMENT'] },
          },
          {
            path: 'raters/:rater_id',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-rater-workspace/assessment-360-rater-workspace.component'
              ).then((m) => m.Assessment360RaterWorkspaceComponent),
            data: { tasks: ['ASSESSMENT_360_MY_ASSESSMENT'] },
          },
          {
            // employees: their own reports, once finalized
            path: 'my-reports',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-my-reports/assessment-360-my-reports.component'
              ).then((m) => m.Assessment360MyReportsComponent),
            data: { tasks: ['ASSESSMENT_360_MY_REPORT'] },
          },
          {
            path: 'my-reports/:instance_id',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-my-reports/assessment-360-my-reports.component'
              ).then((m) => m.Assessment360MyReportsComponent),
            data: { tasks: ['ASSESSMENT_360_MY_REPORT'] },
          },
          {
            path: 'campaigns/:id/reports',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-reports/assessment-360-campaign-reports.component'
              ).then((m) => m.Assessment360CampaignReportsComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            path: 'campaigns/:id/reports/:instance_id',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-campaign-reports/assessment-360-campaign-reports.component'
              ).then((m) => m.Assessment360CampaignReportsComponent),
            data: { tasks: ['ASSESSMENT_360_CAMPAIGN_MANAGE'] },
          },
          {
            // Old single-report link: admins are sent to the campaign's reports page, anyone else
            // (e.g. the subject reading their finalized report) sees just the report
            path: 'instances/:instance_id/report',
            loadComponent: () =>
              import(
                './pages/assessment-360/assessment-360-report/assessment-360-report.component'
              ).then((m) => m.Assessment360ReportComponent),
          },
        ],
      },
      {
        path: '**',
        redirectTo: 'dashboard',
      },
    ],
  },
  { path: '**', redirectTo: 'pages/dashboard' },
];
